/**
 * UI-specific typography components for interface elements
 */

import React from 'react';
import { StyleSheet } from 'react-native';
import { Text, TextProps } from '../Text';
import { useTheme } from '../../themes';
import { colorTokens } from '../../tokens';

export interface UITextProps extends Omit<TextProps, 'variant'> {
  children: React.ReactNode;
}

/**
 * Caption text for small descriptive text
 */
export function Caption({ children, style, ...props }: UITextProps) {
  const captionStyle = [styles.caption, style];

  return (
    <Text variant='caption' style={captionStyle} {...props}>
      {children}
    </Text>
  );
}

/**
 * Label text for form labels and UI labels
 */
export function Label({ children, style, ...props }: UITextProps) {
  const labelStyle = [styles.label, style];

  return (
    <Text variant='label' style={labelStyle} {...props}>
      {children}
    </Text>
  );
}

/**
 * Button text with proper styling for interactive elements
 */
export function ButtonText({ children, style, ...props }: UITextProps) {
  const buttonStyle = [styles.buttonText, style];

  return (
    <Text variant='button' style={buttonStyle} {...props}>
      {children}
    </Text>
  );
}

/**
 * Helper text for form fields and explanatory content
 */
export function HelperText({ children, style, ...props }: UITextProps) {
  const helperStyle = [styles.helperText, style];

  return (
    <Text variant='caption' color='tertiary' style={helperStyle} {...props}>
      {children}
    </Text>
  );
}

/**
 * Error text for validation messages
 */
export function ErrorText({ children, style, ...props }: UITextProps) {
  const errorStyle = [styles.errorText, style];

  return (
    <Text variant='caption' color='error' style={errorStyle} {...props}>
      {children}
    </Text>
  );
}

/**
 * Success text for positive feedback
 */
export function SuccessText({ children, style, ...props }: UITextProps) {
  const successStyle = [styles.successText, style];

  return (
    <Text variant='caption' color='success' style={successStyle} {...props}>
      {children}
    </Text>
  );
}

/**
 * Warning text for cautionary messages
 */
export function WarningText({ children, style, ...props }: UITextProps) {
  const warningStyle = [styles.warningText, style];

  return (
    <Text variant='caption' color='warning' style={warningStyle} {...props}>
      {children}
    </Text>
  );
}

/**
 * Badge text for small status indicators
 */
export function BadgeText({ children, style, ...props }: UITextProps) {
  const { theme } = useTheme();

  const badgeStyle = [
    styles.badgeText,
    {
      backgroundColor: colorTokens.primary[500],
      color: theme.colors.text.inverse,
      paddingHorizontal: theme.spacing.spacing[2],
      paddingVertical: theme.spacing.spacing[1],
      borderRadius: theme.spacing.spacing[3],
    },
    style,
  ];

  return (
    <Text variant='caption' style={badgeStyle} {...props}>
      {children}
    </Text>
  );
}

/**
 * Placeholder text for empty states and form placeholders
 */
export function PlaceholderText({ children, style, ...props }: UITextProps) {
  const placeholderStyle = [styles.placeholderText, style];

  return (
    <Text variant='body' color='tertiary' style={placeholderStyle} {...props}>
      {children}
    </Text>
  );
}

/**
 * Link text for clickable text elements
 */
export function LinkText({ children, style, ...props }: UITextProps) {
  const { theme } = useTheme();

  const linkStyle = [
    styles.linkText,
    {
      color: colorTokens.primary[500],
    },
    style,
  ];

  return (
    <Text variant='body' style={linkStyle} {...props}>
      {children}
    </Text>
  );
}

const styles = StyleSheet.create({
  caption: {
    // Caption-specific styles
  },
  label: {
    // Label-specific styles
  },
  buttonText: {
    // Button text specific styles
    textAlign: 'center',
  },
  helperText: {
    // Helper text specific styles
    marginTop: 4,
  },
  errorText: {
    // Error text specific styles
    marginTop: 4,
  },
  successText: {
    // Success text specific styles
    marginTop: 4,
  },
  warningText: {
    // Warning text specific styles
    marginTop: 4,
  },
  badgeText: {
    // Badge text specific styles
    textAlign: 'center',
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  placeholderText: {
    // Placeholder text specific styles
    fontStyle: 'italic',
  },
  linkText: {
    // Link text specific styles
    textDecorationLine: 'underline',
  },
});
